// Trip edits as events (docs/ORGANISER_PLAN.md §3). The trip seed (packs/<id>/) is the oldest
// write; each *.set carries only the fields that changed, and the projection folds them in server
// order, last writer wins per field. A *.removed is a tombstone that a later *.set revives.
import { z } from 'zod'
import { DayItem, Leg, Place, Stage, TripFact } from './pack/schema'
import { RewardMachineId } from './rewards'

const id = z.string().regex(/^[a-z0-9][a-z0-9-]*$/, 'kebab-case id')
const tripId = id

export const TripMetaSet = z.object({
  tripId,
  // `rewardMachine: null` goes back to the kit's machine (B16); `travellers: null` is every child (B9).
  // `facts` replaces the whole list (B6).
  fields: z
    .object({
      title: z.string().min(1),
      subtitle: z.string(),
      startDate: z.string(),
      endDate: z.string(),
      rewardMachine: RewardMachineId.nullable(),
      travellers: z.array(z.string().min(1)).max(8).nullable(),
      facts: z.array(TripFact).max(40),
    })
    .partial(),
})
// `surprise: null` switches a stage's surprise off.
export const TripStageSet = z.object({ tripId, stageId: id, fields: z.object({ ...Stage.omit({ id: true }).shape, surprise: Stage.shape.surprise.nullable() }).partial() })
export const TripStageRemoved = z.object({ tripId, stageId: id })
export const TripLegSet = z.object({ tripId, legId: id, fields: Leg.omit({ id: true }).partial() })
/** Whether a leg is booked: as set in the editor, else (older legs, the seed) when it has a booking ref. */
export function legBooked(leg: Pick<Leg, 'booked' | 'ref'>): boolean {
  return leg.booked ?? !!leg.ref?.trim()
}
export const TripLegRemoved = z.object({ tripId, legId: id })
export const TripItemSet = z.object({
  tripId,
  itemId: id,
  // `null` clears an optional field (a surprise switched off, a place unlinked).
  fields: z
    .object({
      ...DayItem.omit({ id: true }).shape,
      surprise: DayItem.shape.surprise.nullable(),
      placeId: DayItem.shape.placeId.nullable(),
      time: DayItem.shape.time.nullable(),
      endTime: DayItem.shape.endTime.nullable(),
      sub: DayItem.shape.sub.nullable(),
      award: DayItem.shape.award.nullable(),
      who: DayItem.shape.who.nullable(),
    })
    .partial(),
})
export const TripItemRemoved = z.object({ tripId, itemId: id })
/** A place the family added (from a Maps link or by hand), on top of the kit's library. */
export const TripPlaceSet = z.object({ tripId, placeId: id, fields: Place.omit({ id: true }).partial() })

export const TRIP_EVENTS = {
  'trip.meta.set': TripMetaSet,
  'trip.stage.set': TripStageSet,
  'trip.stage.removed': TripStageRemoved,
  'trip.leg.set': TripLegSet,
  'trip.leg.removed': TripLegRemoved,
  'trip.item.set': TripItemSet,
  'trip.item.removed': TripItemRemoved,
  'trip.place.set': TripPlaceSet,
} as const
export type TripEventType = keyof typeof TRIP_EVENTS

export type TripItemFields = z.infer<typeof TripItemSet>['fields']
